import React, { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import { getConfigOption } from "~/store/selectors/selector.configOptions.jsx";
import { getFeatureFlag } from "~/store/selectors/selector.featureFlags.jsx";
import { getActiveSubtasksIdsByTaskId } from "~/store/selectors/selector.subtasks.jsx";
import { makeGetIsTaskCompletedByIdSelector } from "~/store/selectors/selector.tasks.jsx";
import { hasLoggedUserElevatedPermissions } from "~/store/selectors/selector.users.jsx";
import Angie from "~/utils/angie.jsx";
import SubtasksBodyWithItems from "./SubtasksBodyWithItems.jsx";
import SubtasksBodyWithoutItems from "./SubtasksBodyWithoutItems.jsx";
import { SuggestSubtasksWizard } from "../tasks/SuggestWizard/SuggestSubtasksWizard.js";

const Subtasks = ({ taskId, projectId, convertToTaskReact }) => {
  const [force, setForce] = useState(false);

  const visible = useSelector(hasLoggedUserElevatedPermissions);
  const suggestTasksFF = useSelector((state) => getFeatureFlag(state, "ai_enabled"));
  const aiUserMessagingEnabled = useSelector((state) => getConfigOption(state, "user_messaging_service"));

  const showSuggestButton = suggestTasksFF && aiUserMessagingEnabled;

  const [open, setOpen] = useState(false);

  const handleOpenWizard = useCallback(() => {
    setOpen(true);
    Angie.logger.info("Suggest subtasks initiated");
  }, []);

  const handleCloseWizard = useCallback(() => {
    setOpen(false);
  }, []);

  const getIsTaskCompletedById = useMemo(makeGetIsTaskCompletedByIdSelector, []);

  const subtaskIds = useSelector((state) => getActiveSubtasksIdsByTaskId(state, taskId));
  const isTaskCompleted = useSelector((state) => getIsTaskCompletedById(state, taskId));

  const toggleOnForce = useCallback((e) => {
    e.preventDefault();
    setForce(true);
  }, []);

  const toggleOffForce = useCallback(() => {
    setForce(false);
  }, []);
  return (
    <>
      {!force && subtaskIds.length < 1 ? (
        <SubtasksBodyWithoutItems
          onClick={toggleOnForce}
          isTaskCompleted={isTaskCompleted}
          onSuggest={handleOpenWizard}
          visible={visible}
          showSuggestButton={showSuggestButton}
        />
      ) : (
        <SubtasksBodyWithItems
          convertToTaskReact={convertToTaskReact}
          onCancel={toggleOffForce}
          force={force}
          isTaskCompleted={isTaskCompleted}
          subtaskIds={subtaskIds}
          taskId={taskId}
          projectId={projectId}
          onSuggest={handleOpenWizard}
          visible={visible}
          showSuggestButton={showSuggestButton}
        />
      )}
      {visible && showSuggestButton ? (
        <SuggestSubtasksWizard
          open={open}
          onCancel={handleCloseWizard}
          projectId={projectId}
          taskId={taskId}
        />
      ) : null}
    </>
  );
};

Subtasks.propTypes = {
  projectId: PropTypes.number.isRequired,
  taskId: PropTypes.number.isRequired,
  convertToTaskReact: PropTypes.func,
};

Subtasks.displayName = "Subtasks";

export default Subtasks;
